import { useState, useEffect, Suspense } from "react";

// ─── Auth & données ───────────────────────────────────────────────────────────
import { LOGO_CMOB, CSS } from './data/constants';
import { PAGE_META } from './data/sampleData';
import { useAuth }    from './hooks/useAuth';
import { useAppData } from './hooks/useAppData';
import { useDB }      from './hooks/useDB';

// ─── Shared ───────────────────────────────────────────────────────────────────
import { Login }              from './components/shared/Login';
import { ChangerMotDePasse }  from './components/modules/ChangerMotDePasse';

// ─── Modules directs ─────────────────────────────────────────────────────────
import { DashboardRH, DashboardSalarie }         from './components/modules/Dashboard';
import { useNotifications, ClocheNotifications } from './components/modules/Notifications';

// ─── Navigation ──────────────────────────────────────────────────────────────
import { HubDynamique, HubMonEspace } from './app.nav';
import { ONGLETS_CATALOGUE }          from './app.catalogue';
import {
  ModuleLoading, LazyRechercheGlobale, LazyOrganigramme, LazyPlanning,
  LazyCongesManager, LazyDossierSalaries, LazyGED,
  LazyMaSection, LazyRGPD, LazyChatbotSIRH,
} from './app.lazy';

export default function App() {
  // ── Auth ──────────────────────────────────────────────────────────────────
  const {
    user, loading: authLoading, sessionWarn, inactivityWarn,
    signIn, signOut, prolongerSession, prolongerInactivite,
    getAccessToken, clearMustChangePassword,
  } = useAuth();

  useEffect(() => {}, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Couche données ────────────────────────────────────────────────────────
  const {
    groupsOrder, setGroupsOrder, menuConfig, setMenuConfig,
    menuHidden,  setMenuHidden,  menuLabels, setMenuLabels,
    hubConfig,   setHubConfig,   modulesDesac, setModulesDesac,
    moduleRoles, chatbotConfig, setChatbotConfig, orgaConfig, setOrgaConfig,
    paramsAPI, setParamsAPI, paramsRH, setParamsRH,
    staff, setStaff, postes, setPostes, rup, setRup, // eslint-disable-line no-unused-vars
    events, setEvents, conges, setConges, soldes, setSoldes,
    docs, setDocs, medecine, setMedecine, entretiens, setEntretiens,
    baseDocs, setBaseDocs, users, setUsers, loadingUsers, syncedUsers,
    planningH, setPlanningH, profils, setProfils,
    modelesContrats, setModelesContrats, contratsGen, setContratsGen,
    commPerms, setCommPerms, annonces, setAnnonces, messages, setMessages,
    remunerations, setRemunerations, absencesNP, setAbsencesNP,
    formations, setFormations, benevoles, setBenevoles, reunions, setReunions, // eslint-disable-line no-unused-vars
    onboarding, setOnboarding, alertesConfig, setAlertesConfig, espaceSections,
    adherents, setAdherents, adherentsConfig, setAdherentsConfig,
    accesCoordAdh, setAccesCoordAdh, varPaie, setVarPaie,
    affichage, setAffichage, planFormation, setPlanFormation,
    notesRH, setNotesRH, messagerie, setMessagerie,
    objectifs, setObjectifs, workflows, setWorkflows,
    demandesWF, setDemandesWF, infosAsso, setInfosAsso,
    presences, setPresences, audit, setAudit, connexions, accesCoord,
    resolvedUser, userRoles,
    isAdmin, isRH, isManager, isBureau, isCodir, isSal, // eslint-disable-line no-unused-vars
    myStaffId, myUserId,
    congesEnAttenteMgr, congesEnAttenteRH,
    chatbotVisible, chatIsAdmin, chatIsRH, chatIsManager,
    logConnexion, mkAuditSet,
    auditSetFormations, auditSetMedecine, auditSetDocs, auditSetBenevoles, auditSetRup,
  } = useAppData(user);

  // ── Config indicateurs — état global pour éviter le reset au changement de page ──
  const DEFAULT_DASHBOARD_ACTIFS = [
    'personnel_total','salaries','conges_attente','docs_alert',
    'visites_dues','entretiens_planifies','formations_planifiees','onboardings_en_cours',
  ];
  const [configDashboard, setConfigDashboard] = useDB('cmob_dashboard_indicateurs', DEFAULT_DASHBOARD_ACTIFS);
  const [configKpi,       setConfigKpi]       = useDB('cmob_kpi_actifs', null);

  // ── Audit wrappers (compatibilité HubDynamique) ───────────────────────────
  // eslint-disable-next-line no-unused-vars
  const _auditSetStaff = (fn, action, label, avant=null) => {
    setStaff(prev => {
      const next = typeof fn === 'function' ? fn(prev) : fn;
      if (user) mkAuditSet(setStaff,'Personnel')(fn, action, label, avant);
      return next;
    });
  };

  // ── URL ↔ page sync ──────────────────────────────────────────────────────
  const PAGE_SLUGS = {
    'dashboard':'/','personnel':'/personnel','planning-hub':'/planning',
    'vie-travail':'/vie-au-travail','contrats-hub':'/contrats',
    'collaboration':'/collaboration','club-hub':'/club',
    'organigramme':'/organigramme','evenements':'/evenements',
    'reporting-hub':'/reporting','admin-hub':'/administration',
    'conges-mgr':'/validation-conges','dossiers':'/dossiers-equipe',
    'ged-manager':'/documents','mon-espace':'/mon-espace',
    'ma-section':'/ma-section','assistant':'/assistant','rgpd':'/rgpd',
    'fiches':'/fiches','effectifs':'/effectifs','onboarding':'/onboarding',
    'notes':'/notes-rh','anniversaires':'/anniversaires','paie':'/paie',
    'formations':'/formations','plan-formation':'/plan-formation',
    'unifie':'/planning-unifie','horaires':'/horaires',
    'conges':'/conges','absences':'/absences','medecine':'/medecine',
    'entretiens':'/entretiens','objectifs':'/objectifs',
    'contrats':'/contrats-avenants','rup':'/registre-personnel','cartepro':'/cartes-pro',
    'reunions':'/reunions','communication':'/communication','messagerie':'/messagerie',
    'ged':'/ged','basedocs':'/base-documentaire','workflows':'/workflows',
    'affichage':'/affichage-reglementaire','benevoles':'/benevoles',
    'adherents':'/adherents','sections':'/sections','comptabilite':'/comptabilite',
    'kpis':'/indicateurs-rh','bilan':'/bilan-social','export':'/export',
    'parametres':'/parametres','acces':'/gestion-acces',
    'infos-asso':'/infos-association','alertes':'/alertes','import':'/import',
    'historique':'/historique','test-email':'/test-email',
    'organisation':'/organisation-modules','purge':'/purge',
    'modeles-emails':'/modeles-emails','monitoring':'/monitoring',
  };
  const SLUG_TO_PAGE = Object.fromEntries(Object.entries(PAGE_SLUGS).map(([k,v])=>[v,k]));
  const TAB_TO_HUB   = Object.fromEntries(
    ONGLETS_CATALOGUE.filter(t=>!t.horsHub).map(t=>[t.key,t.hub])
  );

  const [navTarget, setNavTarget] = useState(null);
  const [page, setPage] = useState(() => {
    const slug = window.location.pathname;
    const key  = SLUG_TO_PAGE[slug];
    if (!key) return 'dashboard';
    if (TAB_TO_HUB[key]) return TAB_TO_HUB[key];
    return key;
  });

  const [initialTab] = useState(() => {
    const slug = window.location.pathname;
    const key  = SLUG_TO_PAGE[slug];
    if (key && TAB_TO_HUB[key]) return key;
    const hash       = window.location.hash.replace('#','');
    const parts      = hash.split('/');
    const currentHub = Object.entries(PAGE_SLUGS).find(([,v])=>v===slug)?.[0];
    if (currentHub) {
      const hp = parts.find(p=>p.startsWith('hub-'+currentHub+':'));
      if (hp) return hp.split(':')[1];
    }
    return null;
  });

  useEffect(() => {
    const slug = PAGE_SLUGS[page] || '/';
    if (window.location.pathname !== slug) window.history.pushState(null,'',slug + window.location.hash);
  },[page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onPop = () => {
      const key = SLUG_TO_PAGE[window.location.pathname] || 'dashboard';
      setPage(TAB_TO_HUB[key] ? TAB_TO_HUB[key] : key);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  },[]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── UI state ─────────────────────────────────────────────────────────────
  const [darkMode,      setDarkMode]      = useState(() => localStorage.getItem('cmob_dark') === 'true');
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [editMenu,      setEditMenu]      = useState(false);
  const [dragGroup,     setDragGroup]     = useState(null);
  const [dragOver,      setDragOver]      = useState(null);
  const [dragItem,      setDragItem]      = useState(null);
  const [dragOverItem,  setDragOverItem]  = useState(null);
  const [showMenuPanel, setShowMenuPanel] = useState(false);
  const [newGroupName,  setNewGroupName]  = useState('');
  const [editingLabel,  setEditingLabel]  = useState(null);
  const [labelDraft,    setLabelDraft]    = useState('');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('cmob_dark', darkMode);
  },[darkMode]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleLogin  = () => setPage('dashboard');
  const handleLogout = () => { signOut(); setPage('dashboard'); };
  const navigate = key => {
    // Si la clé est un onglet de hub (ex: 'conges', 'medecine'…),
    // on navigue vers le hub parent et on positionne le hash pour activer le bon onglet
    const hub = TAB_TO_HUB[key];
    if (hub) {
      // Mettre à jour le hash : hub-<hub>:<tab>
      const currentHash = window.location.hash.replace('#', '');
      const parts = currentHash.split('/').filter(p => p && !p.startsWith('hub-' + hub + ':'));
      parts.push('hub-' + hub + ':' + key);
      window.location.hash = parts.join('/');
      setNavTarget(key);
      setPage(hub);
    } else {
      setPage(key);
    }
    setSidebarOpen(false);
  };

  // ── Rôles UI ─────────────────────────────────────────────────────────────
  const roleLabel  = isAdmin?'⚙️ Admin':isRH?'🔐 RH':isManager?'👔 Manager':'👤 Salarié';
  const roleClass  = isAdmin?'role-rh':isRH?'role-rh':isManager?'role-manager':isBureau?'role-rh':'role-sal';
  const ROLE_ICONS = {admin:'⚙️',rh:'🔐',manager:'👔',salarie:'👤',bureau:'🏛️'};
  const roleBadges = userRoles.map(r=>ROLE_ICONS[r]||r).join(' · ');

  // ── Nav ───────────────────────────────────────────────────────────────────
  const NAV_ALL_ITEMS = [
    {key:'dashboard',     icon:'🏠',label:'Tableau de bord',     group:'Principal',             roles:['admin','rh','manager']},
    {key:'personnel',     icon:'👥',label:'Personnel & RH',       group:'Gestion RH',            roles:['admin','rh']},
    {key:'planning-hub',  icon:'📅',label:'Planning',             group:'Gestion RH',            roles:['admin','rh','manager']},
    {key:'vie-travail',   icon:'🌴',label:'Vie au travail',       group:'Gestion RH',            roles:['admin','rh']},
    {key:'contrats-hub',  icon:'📜',label:'Contrats & Légal',     group:'Gestion RH',            roles:['admin','rh']},
    {key:'collaboration', icon:'🗄️',label:'Collaboration & Docs', group:'Ressources',            roles:['admin','rh','manager']},
    {key:'club-hub',      icon:'🏅',label:'Club & Sections',      group:'Sections & Bénévoles',  roles:[]},
    {key:'organigramme',  icon:'🏛️',label:'Organigramme',         group:'Gestion RH',            roles:[]},
    {key:'evenements',    icon:'📅',label:'Planning CMOB',         group:'Principal',             roles:[]},
    {key:'reporting-hub', icon:'📊',label:'Reporting',            group:'Analytique',            roles:['admin','rh']},
    {key:'admin-hub',     icon:'⚙️',label:'Administration',       group:'Administration',        roles:['admin']},
    {key:'conges-mgr',    icon:'✅',label:'Validation congés',    group:'Mon équipe',            roles:['manager']},
    {key:'dossiers',      icon:'👔',label:'Dossiers équipe',      group:'Mon équipe',            roles:['manager']},
    {key:'ged-manager',   icon:'🗂️',label:'Documents',            group:'Mon équipe',            roles:['manager']},
    {key:'mon-espace',    icon:'👤',label:'Mon espace',           group:'Mon espace',            roles:['salarie']},
    {key:'ma-section',    icon:'🏟',label:'Ma section',           group:'Mon espace',            roles:['admin','rh','manager','salarie','bureau']},
    {key:'assistant',     icon:'🤖',label:'Assistant CMOB',       group:'Ressources',            roles:['admin','rh','manager','salarie','benevole']},
    {key:'rgpd',          icon:'🔒',label:'RGPD',                 group:'Ressources',            roles:['admin','rh','manager','salarie','bureau']},
  ];
  const SYSTEM_KEYS = ['dashboard','parametres','accès'];
  const seen = new Set();
  const nav = NAV_ALL_ITEMS.filter(n => {
    if (seen.has(n.key)) return false;
    if ((modulesDesac||[]).includes(n.key) && !SYSTEM_KEYS.includes(n.key)) return false;
    if ((menuHidden||[]).includes(n.key)   && !SYSTEM_KEYS.includes(n.key)) return false;
    const override       = moduleRoles?.[n.key];
    const rolesEffectifs = override?.roles ?? n.roles;
    const extraIds       = override?.extra ?? [];
    const roleOk         = rolesEffectifs.length===0 || rolesEffectifs.some(r=>userRoles.includes(r));
    const extraOk        = extraIds.includes(user?.id);
    if (!isAdmin && !roleOk && !extraOk) return false;
    seen.add(n.key);
    return true;
  });
  const pm            = PAGE_META[page] || PAGE_META['dashboard'];
  const groupsDefault = [...new Set(nav.map(n=>n.group))];
  const customGroups  = (menuConfig.customGroups||[]).filter(g=>!groupsDefault.includes(g));
  const allGroups     = [...groupsDefault,...customGroups];
  const groups        = groupsOrder.length
    ? [...groupsOrder.filter(g=>allGroups.includes(g)),...allGroups.filter(g=>!groupsOrder.includes(g))]
    : allGroups;
  const navCustom = menuConfig.itemGroups||{};

  // ── Menu panel helpers ────────────────────────────────────────────────────
  const SYSTEM_KEYS_PANEL = ['dashboard','parametres','accès'];
  const allItemsUniq = NAV_ALL_ITEMS.filter((v,i,a)=>a.findIndex(x=>x.key===v.key)===i);
  const groupsDispos = [...new Set(allItemsUniq.map(n=>n.group))];
  const toggleHidden = key => setMenuHidden(prev=>{const l=prev||[];return l.includes(key)?l.filter(k=>k!==key):[...l,key];});
  const saveLabel    = key => {setMenuLabels(prev=>({...(prev||{}),[key]:labelDraft.trim()||undefined}));setEditingLabel(null);};
  const resetLabel   = key => {setMenuLabels(prev=>{const n={...(prev||{})};delete n[key];return n;});setEditingLabel(null);};
  const addCustomGroup = () => {
    const name=newGroupName.trim();
    if(!name||groupsDispos.includes(name))return;
    setMenuConfig(prev=>({...prev,customGroups:[...(prev?.customGroups||[]),name]}));
    setNewGroupName('');
  };
  const resetAllMenu = () => {setMenuHidden([]);setMenuLabels({});setGroupsOrder([]);setMenuConfig({});};

  // ── Mobile touch & keyboard ───────────────────────────────────────────────
  useEffect(()=>{
    const h=e=>{
      if(e.key==='Escape'){setSidebarOpen(false);return;}
      if((e.ctrlKey||e.metaKey)&&(e.key==='k'||e.key==='K')){e.preventDefault();navigate('recherche');return;}
      if((e.ctrlKey||e.metaKey)&&(e.key==='h'||e.key==='H')){e.preventDefault();navigate('dashboard');return;}
    };
    window.addEventListener('keydown',h);
    return ()=>window.removeEventListener('keydown',h);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  useEffect(()=>{
    if(window.innerWidth>768)return;
    let sx=0,sy=0;
    const ts=e=>{sx=e.touches[0].clientX;sy=e.touches[0].clientY;};
    const te=e=>{
      const dx=e.changedTouches[0].clientX-sx;
      const dy=e.changedTouches[0].clientY-sy;
      if(Math.abs(dx)>Math.abs(dy)&&Math.abs(dx)>50){
        if(dx>0&&sx<50)setSidebarOpen(true);
        else if(dx<0&&sidebarOpen)setSidebarOpen(false);
      }
    };
    document.addEventListener('touchstart',ts,{passive:true});
    document.addEventListener('touchend',te,{passive:true});
    return()=>{document.removeEventListener('touchstart',ts);document.removeEventListener('touchend',te);};
  },[sidebarOpen]);

  useEffect(()=>{
    const h=()=>{if(window.innerWidth>=768&&window.innerWidth<=1024&&window.innerHeight<window.innerWidth&&sidebarOpen)setSidebarOpen(false);};
    window.addEventListener('orientationchange',h);window.addEventListener('resize',h);
    return()=>{window.removeEventListener('orientationchange',h);window.removeEventListener('resize',h);};
  },[sidebarOpen]);

  useEffect(()=>{
    if(!sidebarOpen)return;
    const h=e=>{const sb=document.querySelector('.sidebar');const btn=document.querySelector('.mobile-menu-btn');if(sb&&!sb.contains(e.target)&&btn&&!btn.contains(e.target))setSidebarOpen(false);};
    document.addEventListener('mousedown',h);document.addEventListener('touchstart',h,{passive:true});
    return()=>{document.removeEventListener('mousedown',h);document.removeEventListener('touchstart',h);};
  },[sidebarOpen]);

  useEffect(()=>{window.scrollTo({top:0,behavior:'smooth'});if(window.innerWidth<=768)setSidebarOpen(false);},[page]);

  useEffect(()=>{
    let last=0;const h=e=>{const now=Date.now();if(now-last<=300)e.preventDefault();last=now;};
    document.addEventListener('touchend',h,{passive:false});return()=>document.removeEventListener('touchend',h);
  },[]);

  useEffect(()=>{
    const d=()=>{const ua=navigator.userAgent;const b=document.body;const w=window.innerWidth;
      b.classList.remove('device-mobile','device-tablet','device-desktop','os-ios','os-android','os-windows');
      if(w<=480)b.classList.add('device-mobile');else if(w<=1024)b.classList.add('device-tablet');else b.classList.add('device-desktop');
      if(/iPad|iPhone|iPod/.test(ua))b.classList.add('os-ios');else if(/android/i.test(ua))b.classList.add('os-android');else if(/windows/i.test(ua))b.classList.add('os-windows');
    };
    d();window.addEventListener('resize',d);return()=>window.removeEventListener('resize',d);
  },[]);

  useEffect(()=>{
    const s=()=>document.documentElement.style.setProperty('--vh',`${window.innerHeight*.01}px`);
    s();window.addEventListener('resize',s);return()=>window.removeEventListener('resize',s);
  },[]);

  // ── Notifications ─────────────────────────────────────────────────────────
  const notifs = useNotifications({ user, staff, conges, docs, medecine, entretiens,
    formations, benevoles, onboarding, messages, annonces, contratsGen, rup, espaceSections,
    alertesConfig });

  // ── allProps HubDynamique ─────────────────────────────────────────────────
  const allProps = {
    staff,setStaff,users,setUsers,postes,setPostes,
    docs,setDocs:auditSetDocs,conges,setConges,soldes,setSoldes,
    medecine,setMedecine:auditSetMedecine,entretiens,setEntretiens,
    formations,setFormations:auditSetFormations,planFormation,setPlanFormation,
    remunerations,setRemunerations,variables:varPaie,setVariables:setVarPaie,
    absencesNP,setAbsencesNP,objectifs,setObjectifs,onboarding,setOnboarding,
    contratsGen,contratsG:contratsGen,setContratsG:setContratsGen,
    planningH,setPlanningH,notesRH,setNotesRH,orgaConfig,setOrgaConfig,
    profils,setProfils,events,setEvents,presences,setPresences,
    baseDocs,setBaseDocs,annonces,setAnnonces,messages,setMessages,
    messagerie,setMessagerie,commPerms,setCommPerms,
    workflows,setWorkflows,demandesWF,setDemandesWF,
    affichage,setAffichage,reunions,setReunions,
    benevoles,setBenevoles:auditSetBenevoles,
    adherents,setAdherents,adherentsConfig,setAdherentsConfig,
    accesCoordAdh,setAccesCoordAdh,
    rup,setRup:auditSetRup,
    modeles:modelesContrats,setModeles:setModelesContrats,
    infosAsso,setInfosAsso,audit,setAudit,connexions,
    modulesDesac,setModulesDesac,chatbotConfig,setChatbotConfig,
    paramsAPI,setParamsAPI,paramsRH,setParamsRH,alertesConfig,setAlertesConfig,
    setStaffAdmin:setStaff,setDocsAdmin:setDocs,setCongesAdmin:setConges,
    setSoldesAdmin:setSoldes,setPlanningHAdmin:setPlanningH,setMedecineAdmin:setMedecine,
    user:resolvedUser,isAdmin,isRH:!!isRH,isManager,isBureau,
    accesCoord,myStaffId,myUserId,hubConfig,setHubConfig,
    getAccessToken,prolongerSession,
    navigate,
    configKpi, setConfigKpi,
  };

  // ── Équipe du manager courant ─────────────────────────────────────────────
  const _equipeUsers = isManager && myUserId
    ? users.filter(u => {
        if (String(u.id) === String(myUserId)) return false;
        const ids = u.managerIds || (u.managerId ? [u.managerId] : []);
        return ids.some(id =>
          String(id) === String(myUserId) ||
          (myStaffId != null && String(id) === String(myStaffId))
        );
      })
    : [];
  const _equipeStaffIds = new Set(_equipeUsers.map(u => u.staffId).filter(Boolean).map(String));
  const staffEquipe      = staff.filter(s => _equipeStaffIds.has(String(s.id)));
  const docsEquipe       = docs.filter(d => _equipeStaffIds.has(String(d.staffId)));
  const medecineEquipe   = medecine.filter(m => _equipeStaffIds.has(String(m.staffId)));
  const entretEquipe     = entretiens.filter(e => _equipeStaffIds.has(String(e.staffId)));
  const formationsEquipe = formations.filter(f => _equipeStaffIds.has(String(f.staffId)));
  const absEquipe        = absencesNP.filter(a => _equipeStaffIds.has(String(a.staffId)));
  const onboardEquipe    = onboarding.filter(o => _equipeStaffIds.has(String(o.staffId)));
  const congesMgrEquipe  = conges.filter(c =>
    (c.managers||[]).some(m => String(m.staffId) === String(myStaffId))
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS}</style>

      {(loadingUsers||authLoading)&&(
        <div style={{position:'fixed',inset:0,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:16,background:'var(--bg2,#f0f4f8)',zIndex:9999}}>
          <img src={LOGO_CMOB} alt="CMOB" style={{maxWidth:160,marginBottom:8}}/>
          <div style={{fontWeight:700,fontSize:'1.1rem',color:'var(--bleu,#003087)'}}>Intranet CMOB</div>
          <div style={{color:'var(--muted,#888)',fontSize:'0.88rem'}}>Connexion à la base de données…</div>
          <div style={{width:200,height:4,background:'#e0e0e0',borderRadius:4,overflow:'hidden'}}>
            <div style={{height:'100%',width:'60%',background:'var(--cyan,#00aeef)',borderRadius:4}}/>
          </div>
        </div>
      )}

      {!loadingUsers&&!authLoading&&!resolvedUser&&(
        <Login onLogin={handleLogin} onLogEvent={logConnexion} signIn={signIn} paramsAPI={paramsAPI} infosAsso={infosAsso}/>
      )}

      {!loadingUsers&&!authLoading&&resolvedUser&&resolvedUser.mustChangePassword&&(
        <div className="login-wrap"><div className="login-card">
          <div className="login-logo"><img src={LOGO_CMOB} alt="Logo CMOB" style={{maxWidth:200,marginBottom:8,display:'block',margin:'0 auto 8px'}}/></div>
          <div className="login-title" style={{fontSize:'1rem'}}>🔐 Changement de mot de passe requis</div>
          <p style={{fontSize:'0.82rem',color:'var(--muted)',marginBottom:16,textAlign:'center'}}>
            Pour des raisons de sécurité, vous devez définir un nouveau mot de passe avant d'accéder à l'intranet.
          </p>
          <ChangerMotDePasse getAccessToken={getAccessToken} prolongerSession={prolongerSession} targetUser={resolvedUser} isAdmin={false} user={resolvedUser} onClose={clearMustChangePassword}/>
          <div style={{marginTop:14,textAlign:'center'}}>
            <button onClick={handleLogout} style={{background:'none',border:'none',cursor:'pointer',color:'var(--muted)',fontSize:'0.8rem',fontFamily:'inherit'}}>← Se déconnecter</button>
          </div>
        </div></div>
      )}

      {!loadingUsers&&!authLoading&&resolvedUser&&!resolvedUser.mustChangePassword&&<>
      <div className="app">

        {inactivityWarn&&user&&(
          <div style={{position:'fixed',top:0,left:0,right:0,zIndex:99999,background:'#92400e',color:'#fff',padding:'12px 20px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',boxShadow:'0 2px 12px rgba(0,0,0,0.3)',fontSize:'0.85rem',fontWeight:600}}>
            <span style={{fontSize:'1.2rem'}}>⏱️</span>
            <span style={{flex:1}}>Déconnexion automatique dans <strong>2 minutes</strong> en raison d'inactivité.</span>
            <button onClick={prolongerInactivite} style={{background:'#fff',color:'#92400e',border:'none',borderRadius:6,padding:'6px 16px',fontWeight:700,cursor:'pointer',fontSize:'0.82rem',whiteSpace:'nowrap'}}>✋ Je suis là — Continuer</button>
          </div>
        )}

        <div className={`sidebar-overlay ${sidebarOpen?'open':''}`} onClick={()=>setSidebarOpen(false)}/>

        {/* Panneau personnalisation menu */}
        {showMenuPanel&&isAdmin&&(
          <div style={{position:'fixed',inset:0,background:'rgba(0,20,60,0.65)',zIndex:2000,display:'flex',alignItems:'flex-start',justifyContent:'flex-end',backdropFilter:'blur(2px)'}}>
            <div style={{width:'100%',maxWidth:520,height:'100vh',background:'var(--bg2)',display:'flex',flexDirection:'column',boxShadow:'-8px 0 40px rgba(0,48,135,0.2)'}}>
              <div style={{padding:'20px 24px 16px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
                <div>
                  <div style={{fontWeight:800,fontSize:'1rem',color:'var(--bleu)'}}>⚙️ Personnalisation du menu</div>
                  <div style={{fontSize:'0.75rem',color:'var(--muted)',marginTop:2}}>Masquer · Renommer · Groupes personnalisés — sauvegardé Supabase</div>
                </div>
                <button onClick={()=>setShowMenuPanel(false)} style={{background:'var(--bg3)',border:'1px solid var(--border)',width:34,height:34,borderRadius:'50%',cursor:'pointer',fontSize:'1rem',color:'var(--muted)'}}>✕</button>
              </div>
              <div style={{flex:1,overflowY:'auto',padding:'16px 24px'}}>
                <div style={{marginBottom:20}}>
                  <div style={{fontWeight:700,fontSize:'0.78rem',color:'var(--bleu)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>➕ Créer un groupe</div>
                  <div style={{display:'flex',gap:8}}>
                    <input value={newGroupName} onChange={e=>setNewGroupName(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addCustomGroup()} placeholder="Nom du groupe…"
                      style={{flex:1,padding:'8px 12px',borderRadius:8,border:'1.5px solid var(--border)',fontFamily:'inherit',fontSize:'0.85rem',background:'var(--bg)',color:'var(--text)'}}/>
                    <button onClick={addCustomGroup} disabled={!newGroupName.trim()} className="btn btn-primary btn-sm">Créer</button>
                  </div>
                  {(menuConfig?.customGroups||[]).map(g=>(
                    <div key={g} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'6px 10px',marginTop:6,borderRadius:7,background:'rgba(0,174,239,0.06)',border:'1px solid rgba(0,174,239,0.2)'}}>
                      <span style={{fontSize:'0.82rem',fontWeight:600,color:'var(--bleu)'}}>📂 {g}</span>
                      <button onClick={()=>{setMenuConfig(p=>({...p,customGroups:(p?.customGroups||[]).filter(x=>x!==g)}));setGroupsOrder(p=>(p||[]).filter(x=>x!==g));}} style={{background:'none',border:'none',color:'#dc2626',cursor:'pointer',fontSize:'0.8rem'}}>✕</button>
                    </div>
                  ))}
                </div>
                {groupsDispos.map(g=>(
                  <div key={g} style={{marginBottom:18}}>
                    <div style={{fontWeight:700,fontSize:'0.78rem',color:'var(--bleu)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6,paddingBottom:4,borderBottom:'1px solid var(--border)'}}>
                      {g} <span style={{fontWeight:400,color:'var(--muted)',textTransform:'none'}}>({allItemsUniq.filter(n=>n.group===g).length})</span>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',gap:3}}>
                      {allItemsUniq.filter(n=>n.group===g).map(n=>{
                        const hidden=(menuHidden||[]).includes(n.key);
                        const label=(menuLabels||{})[n.key];
                        const isSys=SYSTEM_KEYS_PANEL.includes(n.key);
                        const isEdit=editingLabel===n.key;
                        return(
                          <div key={n.key} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',borderRadius:8,background:hidden?'transparent':'var(--bg3)',border:'1px solid var(--border)',opacity:hidden?0.45:1,transition:'all 0.15s'}}>
                            <button onClick={()=>!isSys&&toggleHidden(n.key)} disabled={isSys} title={isSys?'Système':hidden?'Afficher':'Masquer'}
                              style={{background:'none',border:'none',cursor:isSys?'not-allowed':'pointer',fontSize:'0.95rem',flexShrink:0,opacity:isSys?0.3:1}}>
                              {hidden?'🙈':'👁️'}
                            </button>
                            <span style={{flexShrink:0}}>{n.icon}</span>
                            {isEdit?(
                              <div style={{flex:1,display:'flex',gap:5}}>
                                <input autoFocus value={labelDraft} onChange={e=>setLabelDraft(e.target.value)}
                                  onKeyDown={e=>{if(e.key==='Enter')saveLabel(n.key);if(e.key==='Escape')setEditingLabel(null);}}
                                  style={{flex:1,padding:'3px 8px',borderRadius:6,border:'1.5px solid var(--cyan)',fontFamily:'inherit',fontSize:'0.82rem',background:'var(--bg)',color:'var(--text)'}}/>
                                <button onClick={()=>saveLabel(n.key)} style={{padding:'3px 10px',borderRadius:6,border:'none',background:'var(--bleu)',color:'#fff',cursor:'pointer',fontSize:'0.75rem',fontFamily:'inherit',fontWeight:700}}>✓</button>
                                {label&&<button onClick={()=>resetLabel(n.key)} style={{padding:'3px 8px',borderRadius:6,border:'1px solid var(--border)',background:'none',color:'var(--muted)',cursor:'pointer',fontSize:'0.72rem',fontFamily:'inherit'}}>Reset</button>}
                                <button onClick={()=>setEditingLabel(null)} style={{padding:'3px 8px',borderRadius:6,border:'1px solid var(--border)',background:'none',color:'var(--muted)',cursor:'pointer',fontSize:'0.75rem',fontFamily:'inherit'}}>✕</button>
                              </div>
                            ):(
                              <div style={{flex:1}}>
                                <span style={{fontSize:'0.82rem',fontWeight:600,color:hidden?'var(--muted)':'var(--text)',textDecoration:hidden?'line-through':'none'}}>{label||n.label}</span>
                                {label&&<span style={{fontSize:'0.68rem',color:'var(--muted)',marginLeft:6,fontStyle:'italic'}}>(orig: {n.label})</span>}
                              </div>
                            )}
                            {!isEdit&&(
                              <div style={{display:'flex',gap:4,alignItems:'center'}}>
                                {!hidden&&<button onClick={()=>{setEditingLabel(n.key);setLabelDraft(label||n.label);}} title="Renommer" style={{background:'none',border:'none',cursor:'pointer',fontSize:'0.78rem',color:'var(--muted)',flexShrink:0,padding:2}}>✏️</button>}
                                <select value={(menuConfig?.itemGroups||{})[n.key]||n.group}
                                  onChange={e=>{const ng=e.target.value;const ni={...(menuConfig?.itemGroups||{}),[n.key]:ng};if(ng===n.group)delete ni[n.key];setMenuConfig(p=>({...p,itemGroups:ni}));}}
                                  title="Déplacer dans un groupe"
                                  style={{padding:'2px 4px',borderRadius:5,border:'1px solid var(--border)',fontFamily:'inherit',fontSize:'0.68rem',color:'var(--muted)',background:'var(--bg)',cursor:'pointer',maxWidth:110}}>
                                  {[...groupsDispos,...(menuConfig?.customGroups||[])].map(grp=><option key={grp} value={grp}>{grp}</option>)}
                                </select>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{padding:'14px 24px',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
                <button onClick={resetAllMenu} style={{padding:'7px 16px',borderRadius:8,border:'1px solid #dc2626',background:'rgba(220,38,38,0.07)',color:'#dc2626',cursor:'pointer',fontSize:'0.8rem',fontWeight:700,fontFamily:'inherit'}}>🔄 Réinitialiser</button>
                <div style={{fontSize:'0.72rem',color:'var(--muted)',textAlign:'center'}}>{(menuHidden||[]).length} masqué(s) · {Object.keys(menuLabels||{}).length} renommé(s)</div>
                <button onClick={()=>setShowMenuPanel(false)} className="btn btn-primary">✅ Fermer</button>
              </div>
            </div>
          </div>
        )}

        {/* SIDEBAR */}
        <nav className={`sidebar ${sidebarOpen?'open':''}`}>
          <div className="sidebar-logo"><img src={LOGO_CMOB} alt="Logo CMOB" style={{maxWidth:140,width:'100%',display:'block',margin:'0 auto'}}/></div>
          <div className="sidebar-user">
            <div className="sidebar-user-name">{user.prenom} {user.nom.split(' ')[0]}</div>
            <div className={`sidebar-user-role ${roleClass}`}>{roleBadges}</div>
          </div>
          {isAdmin&&(
            <div style={{padding:'6px 10px 4px',display:'flex',gap:4}}>
              <button onClick={()=>setEditMenu(v=>!v)} style={{flex:1,padding:'7px 8px',borderRadius:7,fontSize:'0.72rem',fontWeight:600,background:editMenu?'rgba(0,174,239,0.3)':'rgba(255,255,255,0.07)',color:'rgba(255,255,255,0.8)',border:'1px solid rgba(255,255,255,0.15)',cursor:'pointer',display:'flex',alignItems:'center',gap:4,justifyContent:'center'}}>
                {editMenu?'✅ OK':'⠿ Réordonner'}
              </button>
              <button onClick={()=>setShowMenuPanel(true)} style={{padding:'7px 10px',borderRadius:7,fontSize:'0.8rem',fontWeight:600,background:'rgba(255,255,255,0.07)',color:'rgba(255,255,255,0.8)',border:'1px solid rgba(255,255,255,0.15)',cursor:'pointer'}} title="Personnaliser le menu">⚙️</button>
            </div>
          )}
          {groups.map(g=>{
            const isCustomGroup=!groupsDefault.includes(g);
            const itemOrder=(menuConfig.itemOrder||{})[g];
            const sortByOrder=items=>{
              if(!itemOrder?.length)return items;
              return [...itemOrder.map(k=>items.find(n=>n.key===k)).filter(Boolean),...items.filter(n=>!itemOrder.includes(n.key))];
            };
            // eslint-disable-next-line no-unused-vars
            const visibleItems=editMenu?nav:sortByOrder(nav.filter(n=>(navCustom[n.key]||n.group)===g).filter((v,i,a)=>a.findIndex(x=>x.key===v.key)===i));
            const displayItems=sortByOrder(nav.filter(n=>(navCustom[n.key]||n.group)===g).filter((v,i,a)=>a.findIndex(x=>x.key===v.key)===i));
            return(
              <div className="nav-section" key={g}
                draggable={editMenu}
                onDragStart={editMenu?()=>setDragGroup(g):undefined}
                onDragOver={editMenu?e=>{e.preventDefault();setDragOver(g);}:undefined}
                onDragLeave={editMenu?()=>setDragOver(null):undefined}
                onDragEnd={editMenu?()=>{setDragGroup(null);setDragOver(null);setDragItem(null);setDragOverItem(null);}:undefined}
                onDrop={editMenu?e=>{e.preventDefault();if(!dragGroup||dragGroup===g){setDragOver(null);return;}const r=[...groups];const from=r.indexOf(dragGroup);const to=r.indexOf(g);r.splice(from,1);r.splice(to,0,dragGroup);setGroupsOrder(r);setDragGroup(null);setDragOver(null);}:undefined}
                style={{opacity:dragGroup===g?0.35:1,outline:dragOver===g&&dragGroup!==g?'2px dashed rgba(0,174,239,0.7)':'none',borderRadius:dragOver===g?6:0,transition:'opacity 0.15s, outline 0.1s',cursor:editMenu?'grab':'default',background:dragOver===g&&dragGroup!==g?'rgba(0,174,239,0.06)':'transparent'}}>
                <div className="nav-label" style={{display:'flex',alignItems:'center',gap:6,userSelect:'none',justifyContent:'space-between'}}>
                  <div style={{display:'flex',alignItems:'center',gap:5}}>{editMenu&&<span style={{opacity:0.4,fontSize:'0.85rem',cursor:'grab'}}>⠿</span>}{g}</div>
                  {editMenu&&isCustomGroup&&<button onClick={()=>{const nc=(menuConfig.customGroups||[]).filter(x=>x!==g);const no=groups.filter(x=>x!==g);setMenuConfig({...menuConfig,customGroups:nc});setGroupsOrder(no);}} style={{background:'none',border:'none',color:'rgba(220,38,38,0.7)',cursor:'pointer',fontSize:'0.8rem',padding:'0 2px'}} title="Supprimer ce groupe">✕</button>}
                </div>
                {displayItems.map(n=>(
                  <a key={n.key} href={PAGE_SLUGS[n.key]||'/'} className={`nav-item ${page===n.key?'active':''}`}
                    onClick={e=>{if(editMenu)e.preventDefault();}}
                    draggable={editMenu}
                    onDragStart={editMenu?e=>{e.stopPropagation();e.dataTransfer.setData('itemKey',n.key);setDragItem({key:n.key,group:g});setDragOverItem(null);}:undefined}
                    onDragOver={editMenu?e=>{e.preventDefault();e.stopPropagation();if(dragItem&&dragItem.key!==n.key)setDragOverItem(n.key);}:undefined}
                    onDragLeave={editMenu?()=>setDragOverItem(null):undefined}
                    onDrop={editMenu?e=>{e.preventDefault();e.stopPropagation();const key=e.dataTransfer.getData('itemKey');if(!key||key===n.key){setDragItem(null);setDragOverItem(null);return;}const io=menuConfig.itemOrder||{};const ig=menuConfig.itemGroups||{};const cur=displayItems.map(x=>x.key);const wo=cur.filter(k=>k!==key);wo.splice(wo.indexOf(n.key),0,key);setMenuConfig({...menuConfig,itemGroups:{...ig,[key]:g},itemOrder:{...io,[g]:wo}});setDragItem(null);setDragOverItem(null);}:undefined}
                    style={{display:'flex',alignItems:'center',textDecoration:'none',color:'var(--cyan)',cursor:editMenu?'grab':'pointer',opacity:dragItem?.key===n.key?0.35:editMenu?0.85:1,outline:dragOverItem===n.key?'2px dashed rgba(0,174,239,0.7)':'none',borderRadius:dragOverItem===n.key?5:0,transition:'opacity 0.15s, outline 0.1s'}}>
                    <span className="nav-icon">{n.icon}</span>
                    {(menuLabels||{})[n.key]||n.label}
                    {!editMenu&&n.key==='conges'&&congesEnAttenteRH>0&&<span style={{marginLeft:'auto',background:'var(--danger)',color:'#fff',borderRadius:'50%',padding:'1px 7px',fontSize:'0.65rem',fontWeight:700}}>{congesEnAttenteRH}</span>}
                    {!editMenu&&n.key==='conges-mgr'&&congesEnAttenteMgr>0&&<span style={{marginLeft:'auto',background:'var(--danger)',color:'#fff',borderRadius:'50%',padding:'1px 7px',fontSize:'0.65rem',fontWeight:700}}>{congesEnAttenteMgr}</span>}
                  </a>
                ))}
                {editMenu&&(
                  <div onDragOver={e=>e.preventDefault()}
                    onDrop={e=>{e.stopPropagation();const key=e.dataTransfer.getData('itemKey');if(!key)return;const io=menuConfig.itemOrder||{};const ig=menuConfig.itemGroups||{};const cur=displayItems.map(x=>x.key).filter(k=>k!==key);cur.push(key);setMenuConfig({...menuConfig,itemGroups:{...ig,[key]:g},itemOrder:{...io,[g]:cur}});setDragItem(null);setDragOverItem(null);}}
                    style={{minHeight:20,borderRadius:5,margin:'2px 6px',border:'1px dashed rgba(255,255,255,0.1)',fontSize:'0.67rem',color:'rgba(255,255,255,0.25)',textAlign:'center',padding:'3px',lineHeight:'14px'}}>↓ déposer un module ici</div>
                )}
              </div>
            );
          })}
          {editMenu&&isAdmin&&(
            <div style={{padding:'8px 10px'}}>
              <div style={{fontSize:'0.72rem',color:'rgba(255,255,255,0.4)',marginBottom:6,textAlign:'center'}}>Nouveau groupe</div>
              <div style={{display:'flex',gap:5}}>
                <input id="new-group-input" placeholder="Nom du groupe..." style={{flex:1,padding:'5px 8px',borderRadius:6,border:'1px solid rgba(255,255,255,0.2)',background:'rgba(255,255,255,0.08)',color:'#fff',fontSize:'0.75px'}}
                  onKeyDown={e=>{if(e.key!=='Enter')return;const val=e.target.value.trim();if(!val||groups.includes(val))return;setMenuConfig({...menuConfig,customGroups:[...(menuConfig.customGroups||[]),val]});setGroupsOrder([...groups,val]);e.target.value='';}}/>
                <button onClick={()=>{const inp=document.getElementById('new-group-input');const val=inp?.value?.trim();if(!val||groups.includes(val))return;setMenuConfig({...menuConfig,customGroups:[...(menuConfig.customGroups||[]),val]});setGroupsOrder([...groups,val]);if(inp)inp.value='';}} style={{padding:'5px 10px',borderRadius:6,background:'rgba(0,174,239,0.4)',border:'none',color:'#fff',cursor:'pointer',fontSize:'0.8rem',fontWeight:700}}>+</button>
              </div>
              <div style={{marginTop:8,fontSize:'0.67rem',color:'rgba(255,255,255,0.3)',textAlign:'center',lineHeight:1.4}}>Glissez les modules dans vos groupes</div>
              <button onClick={()=>{setGroupsOrder(groupsDefault);setMenuConfig({});}} style={{marginTop:6,width:'100%',padding:'4px',borderRadius:5,background:'rgba(220,38,38,0.2)',border:'1px solid rgba(220,38,38,0.3)',color:'rgba(220,38,38,0.8)',cursor:'pointer',fontSize:'0.7rem'}}>↺ Réinitialiser</button>
            </div>
          )}
          <div className="sidebar-footer">
            <button onClick={()=>setDarkMode(d=>!d)} title={darkMode?'Mode clair':'Mode sombre'} style={{background:'none',border:'1px solid var(--border)',borderRadius:8,padding:'6px 10px',cursor:'pointer',fontSize:'1rem',color:'var(--text)',marginRight:6}}>
              {darkMode?'☀️':'🌙'}
            </button>
            <button className="btn-logout" onClick={handleLogout}>⬅ Se déconnecter</button>
            <div className="sidebar-footer-addr">
              <div>📍 {paramsAPI?.adresse_asso?.split('—')[0]?.trim()||'2 rue Léo Lagrange'}</div>
              <div>{paramsAPI?.adresse_asso?.split('—').slice(1).join('—').trim()||'33530 Bassens'}{paramsAPI?.telephone_asso?` · ${paramsAPI.telephone_asso}`:' · 05 57 77 39 77'}</div>
            </div>
          </div>
        </nav>

        {/* MAIN */}
        <div className="main">
          <div className="topbar">
            <div className="topbar-left"><h2>{pm.title}</h2><div className="topbar-sub">{pm.sub}</div></div>
            <div className="topbar-right">
              <span className={`topbar-role ${isAdmin?'rh':isRH?'rh':isManager?'rh':'sal'}`}>{roleLabel}</span>
              <LazyRechercheGlobale onNavigate={navigate} staff={staff} conges={conges} docs={docs} medecine={medecine} entretiens={entretiens} formations={formations} benevoles={benevoles} baseDocs={baseDocs} contrats={contratsGen} messages={messages} users={users} profils={profils}/>
              <ClocheNotifications notifs={notifs} onNavigate={navigate} user={resolvedUser}/>
              {!syncedUsers&&<span className="topbar-sync-badge no-print" style={{background:'rgba(180,83,9,0.12)',color:'var(--warning)',borderColor:'rgba(180,83,9,0.2)'}}>⚠️ Hors-ligne</span>}
              {syncedUsers&&<span className="topbar-sync-badge no-print" style={{background:'rgba(26,127,55,0.08)',color:'var(--success)',borderColor:'rgba(26,127,55,0.2)'}}>🟢 Sync</span>}
              <span className="topbar-badge no-print">{new Date().toLocaleDateString('fr-FR',{day:'numeric',month:'long',year:'numeric'})}</span>
            </div>
          </div>
          <div key={page} className="content">
            <Suspense fallback={<ModuleLoading/>}>
              {(isRH||isCodir)&&page==='dashboard'&&<DashboardRH staff={staff} events={events} conges={conges} docs={docs} medecine={medecine} entretiens={entretiens} sessionWarn={sessionWarn} prolongerSession={prolongerSession} handleLogout={handleLogout} remunerations={remunerations} formations={formations} benevoles={benevoles} absencesNP={absencesNP} onboarding={onboarding} contratsGen={contratsGen} onNavigate={navigate} configIndicateurs={configDashboard} setConfigIndicateurs={setConfigDashboard}/>}
              {['personnel','planning-hub','vie-travail','contrats-hub','collaboration','club-hub','reporting-hub','admin-hub'].includes(page)&&(()=>{
                if(!isAdmin&&page==='admin-hub')return null;
                if(!isRH&&['personnel','vie-travail','contrats-hub','reporting-hub'].includes(page))return null;
                return <HubDynamique key={page} hubKey={page} hubConfig={hubConfig} allProps={allProps} initialTab={navTarget || initialTab} pageSlugs={PAGE_SLUGS}/>;
              })()}
              {page==='organigramme'&&<LazyOrganigramme staff={staff} isRH={isRH} orgaConfig={orgaConfig} user={resolvedUser} myStaffId={myStaffId} myUserId={myUserId} accesCoord={accesCoord} profils={users}/>}
              {page==='evenements'&&<LazyPlanning events={events} setEvents={setEvents} user={resolvedUser} users={users} staff={staff}/>}
              {isManager&&!isRH&&!isCodir&&page==='dashboard'&&<DashboardRH staff={staffEquipe} events={events} conges={congesMgrEquipe} docs={docsEquipe} medecine={medecineEquipe} entretiens={entretEquipe} sessionWarn={sessionWarn} prolongerSession={prolongerSession} handleLogout={handleLogout} remunerations={remunerations} formations={formationsEquipe} benevoles={benevoles} absencesNP={absEquipe} onboarding={onboardEquipe} contratsGen={contratsGen} onNavigate={navigate} configIndicateurs={configDashboard} setConfigIndicateurs={setConfigDashboard}/>}
              {isManager&&page==='conges-mgr'&&<LazyCongesManager user={user} conges={conges} setConges={setConges} paramsAPI={paramsAPI} staff={staff}/>}
              {isManager&&!isRH&&page==='dossiers'&&<LazyDossierSalaries user={user} users={users} staff={staff} docs={docs} medecine={medecine} entretiens={entretiens} conges={conges} planningH={planningH} postes={postes} remunerations={remunerations} absencesNP={absencesNP} formations={formations} benevoles={benevoles} onboarding={onboarding}/>}
              {isManager&&isRH&&page==='dossiers'&&<LazyDossierSalaries user={resolvedUser} users={users} staff={staff} docs={docs} medecine={medecine} entretiens={entretiens} conges={conges} planningH={planningH} postes={postes} remunerations={remunerations} absencesNP={absencesNP} formations={formations} benevoles={benevoles} onboarding={onboarding} isRH={true}/>}
              {isManager&&!isRH&&page==='ged-manager'&&<LazyGED user={user} users={users} staff={staff}/>}
              {isManager&&isRH&&page==='ged-manager'&&<LazyGED user={resolvedUser} users={users} staff={staff}/>}
              {isSal&&!isRH&&!isManager&&page==='dashboard'&&<DashboardSalarie user={user} conges={conges} soldes={soldes} docs={docs} medecine={medecine} entretiens={entretiens} formations={formations} staff={staff} onNavigate={navigate}/>}
              {isSal&&page==='mon-espace'&&<HubMonEspace user={user} conges={conges} setConges={setConges} soldes={soldes} docs={docs} medecine={medecine} entretiens={entretiens} setEntretiens={setEntretiens} formations={formations} planningH={planningH} presences={presences} setPresences={setPresences} staff={staff} profils={profils} setProfils={setProfils} setStaff={setStaff} users={users} setUsers={setUsers} baseDocs={baseDocs} paramsAPI={paramsAPI} infosAsso={infosAsso} paramsRH={paramsRH} isManager={isManager} isRH={isRH}/>}
              {page==='ma-section'&&<LazyMaSection user={user} staff={staff} isRH={isRH} isAdmin={isAdmin} isBureau={isBureau} events={events} setEvents={setEvents} adherents={adherents} adherentsConfig={adherentsConfig} setAdherents={setAdherents} accesCoordAdh={accesCoordAdh} setAccesCoordAdh={setAccesCoordAdh}/>}
              {page==='rgpd'&&<LazyRGPD user={user} staff={staff} users={users} isAdmin={isAdmin} isRH={isRH}/>}
              {page==='assistant'&&chatbotVisible&&<LazyChatbotSIRH user={user} staff={staff} conges={conges} soldes={soldes} docs={docs} formations={formations} workflows={workflows} objectifs={objectifs} infosAsso={infosAsso} remunerations={remunerations} profils={profils} paramsAPI={paramsAPI} isAdmin={chatIsAdmin} isRH={chatIsRH} isManager={chatIsManager}/>}
              {page==='assistant'&&!chatbotVisible&&(
                <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'60vh',flexDirection:'column',gap:12,color:'var(--muted)'}}>
                  <div style={{fontSize:'3rem'}}>🔒</div>
                  <div style={{fontWeight:600}}>Accès au chatbot non autorisé</div>
                  <div style={{fontSize:'0.82rem'}}>Contactez votre administrateur pour obtenir l'accès.</div>
                </div>
              )}
            </Suspense>
          </div>
        </div>
        <button className="mobile-menu-btn no-print" onClick={()=>setSidebarOpen(o=>!o)}>{sidebarOpen?'✕':'☰'}</button>
      </div>
      </>}
    </>
  );
}

export { ONGLETS_CATALOGUE } from './app.catalogue';
